import { useEffect, useRef, useState } from 'react';

function useCountUp(end: number, duration = 2000, decimals = 0, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(parseFloat((eased * end).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, end, duration, decimals]);
  return count;
}

const metricas = [
  {
    valor: 10000,
    sufixo: '+',
    label: 'Mulheres transformadas',
    descricao: 'em 12 países ao redor do mundo',
    icone: 'ri-group-3-line',
    cor: 'from-gold-500 to-amber-400',
    grande: true,
    decimais: 0,
    formatar: (n: number) => `${(n / 1000).toFixed(0)}k`,
  },
  {
    valor: 97,
    sufixo: '%',
    label: 'Recomendam o método',
    descricao: 'de satisfação nas avaliações',
    icone: 'ri-heart-fill',
    cor: 'from-rose-500 to-pink-400',
    grande: false,
    decimais: 0,
    formatar: (n: number) => `${Math.floor(n)}`,
  },
  {
    valor: 15,
    sufixo: '+',
    label: 'Anos de prática clínica',
    descricao: 'em PNL e performance mental',
    icone: 'ri-award-line',
    cor: 'from-amber-500 to-yellow-400',
    grande: false,
    decimais: 0,
    formatar: (n: number) => `${Math.floor(n)}`,
  },
  {
    valor: 4.9,
    sufixo: '★',
    label: 'Avaliação média',
    descricao: 'baseada em +2.400 reviews',
    icone: 'ri-star-fill',
    cor: 'from-gold-400 to-amber-300',
    grande: false,
    decimais: 1,
    formatar: (n: number) => n.toFixed(1),
  },
  {
    valor: 8,
    sufixo: ' sem.',
    label: 'Para resultado profundo',
    descricao: 'tempo médio de transformação',
    icone: 'ri-time-line',
    cor: 'from-teal-500 to-emerald-400',
    grande: false,
    decimais: 0,
    formatar: (n: number) => `${Math.floor(n)}`,
  },
  {
    valor: 50,
    sufixo: '+',
    label: 'Técnicas aplicadas',
    descricao: 'do arsenal de PNL avançada',
    icone: 'ri-tools-line',
    cor: 'from-violet-500 to-purple-400',
    grande: false,
    decimais: 0,
    formatar: (n: number) => `${Math.floor(n)}`,
  },
];

function MetricaItem({ m, active, delay }: { m: typeof metricas[0]; active: boolean; delay: number }) {
  const count = useCountUp(m.valor, 2000, m.decimais, active);
  const display = m.formatar(count);

  return (
    <div
      className={`relative group flex flex-col items-center text-center p-8 rounded-3xl border border-white/6 bg-white/3 hover:bg-white/5 transition-all duration-500 overflow-hidden ${
        m.grande ? 'lg:col-span-2 lg:row-span-1' : ''
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* glow de fundo */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${m.cor} blur-3xl`}
        style={{ opacity: 0.04 }}
      />

      {/* ícone */}
      <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${m.cor} mb-6 flex-shrink-0`}>
        <i className={`${m.icone} text-2xl text-white`} />
      </div>

      {/* número */}
      <div className={`font-display font-black leading-none mb-3 bg-gradient-to-r ${m.cor} bg-clip-text text-transparent ${m.grande ? 'text-7xl lg:text-8xl' : 'text-5xl lg:text-6xl'}`}>
        {display}{m.sufixo}
      </div>

      {/* label */}
      <p className="text-white font-bold text-base font-['Montserrat'] mb-1">
        {m.label}
      </p>
      <p className="text-white/35 text-xs font-['Montserrat'] leading-relaxed">
        {m.descricao}
      </p>

      {/* linha decorativa bottom */}
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-0 group-hover:w-1/2 bg-gradient-to-r ${m.cor} transition-all duration-500 rounded-full`} />
    </div>
  );
}

export default function ResultadosNumeros() {
  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    const hEl = headerRef.current;
    if (!el || !hEl) return;

    const obsContent = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setActive(true); obsContent.disconnect(); } },
      { threshold: 0.1 }
    );
    const obsHeader = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHeaderVisible(true); obsHeader.disconnect(); } },
      { threshold: 0.2 }
    );

    obsContent.observe(el);
    obsHeader.observe(hEl);
    return () => { obsContent.disconnect(); obsHeader.disconnect(); };
  }, []);

  return (
    <section className="relative bg-[#060606] py-24 lg:py-32 overflow-hidden">
      {/* Fundo decorativo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/15 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/15 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-500/3 rounded-full blur-3xl" />
        {/* grade de pontos sutil */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'radial-gradient(circle, #d4a84b 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-gradient-to-r from-transparent to-gold-400/50" />
            <span className="text-gold-400 text-xs font-semibold tracking-[0.3em] uppercase font-['Montserrat']">
              Impacto em Números
            </span>
            <div className="h-px w-10 bg-gradient-to-l from-transparent to-gold-400/50" />
          </div>

          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-6">
            Resultados que
            <br />
            <span className="bg-gradient-to-r from-gold-400 via-amber-300 to-gold-500 bg-clip-text text-transparent italic">
              falam por si
            </span>
          </h2>

          <p className="text-white/45 text-lg font-['Montserrat'] font-light max-w-xl mx-auto leading-relaxed">
            Mais de uma década de método aplicado. Os números refletem histórias reais de mulheres que escolheram se tornar sua melhor versão.
          </p>
        </div>

        {/* Grid de métricas */}
        <div
          ref={ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
        >
          {metricas.map((m, i) => (
            <MetricaItem key={m.label} m={m} active={active} delay={i * 80} />
          ))}
        </div>

        {/* Barra de depoimento */}
        <div
          className={`mt-14 flex flex-col md:flex-row items-center gap-6 bg-gradient-to-r from-gold-500/8 to-gold-300/4 border border-gold-500/15 rounded-2xl p-6 lg:p-8 transition-all duration-700 delay-700 ${
            active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex -space-x-3 flex-shrink-0">
            {[
              'https://public.readdy.ai/ai/img_res/78ba0074a730a9fbbfe0ed85c9153025.jpg',
              'https://readdy.ai/api/search-image?query=successful%20businesswoman%2038%20dark%20hair%20elegant%20minimal%20portrait&width=48&height=48&seq=soc-proof-a2&orientation=squarish',
              'https://readdy.ai/api/search-image?query=confident%20mature%20woman%20executive%2045%20professional%20portrait&width=48&height=48&seq=soc-proof-a3&orientation=squarish',
              'https://readdy.ai/api/search-image?query=elegant%20brazilian%20woman%20professional%2035%20smiling%20portrait&width=48&height=48&seq=soc-proof-a4&orientation=squarish',
              'https://readdy.ai/api/search-image?query=strong%20empowered%20woman%2042%20radiant%20smile%20executive%20look&width=48&height=48&seq=soc-proof-a5&orientation=squarish',
            ].map((src, i) => (
              <img
                key={i}
                src={src}
                alt="Aluna"
                className="w-10 h-10 rounded-full border-2 border-[#060606] object-cover"
              />
            ))}
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="text-white/70 text-sm font-['Montserrat'] italic leading-relaxed">
              "Em 8 semanas percebi que não era falta de esforço — era falta de alinhamento mental. O método me deu isso."
            </p>
            <p className="text-gold-400 text-xs font-semibold font-['Montserrat'] mt-1.5">
              — Mariana A., VP de Operações · Rio de Janeiro
            </p>
          </div>
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-gold-400 text-sm" />
              ))}
            </div>
            <span className="text-white/30 text-xs font-['Montserrat']">Avaliação verificada</span>
          </div>
        </div>
      </div>
    </section>
  );
}
